import { collection, doc } from 'firebase/firestore'
import { deleteDoc, getDocs, onSnapshot, setDoc, updateDoc } from '@/lib/metered'
import { db } from '@/lib/firebase'
import { recordChange, type Actor } from '@/lib/activity'
import { useSharedCollection } from '@/lib/sharedCollection'
import type { StaffMember } from './types'

/** `enabled` mirrors `useOwners` and `useDecisions` — see the note on `useSharedCollection`. */
export function useStaff(enabled = true) {
  const { rows, loading, error } = useSharedCollection<StaffMember>(
    'staff',
    (onRows, onError) => onSnapshot(
      collection(db, 'staff'),
      (snap) => onRows(
        snap.docs
          .map((d) => ({ id: d.id, ...d.data() }) as StaffMember)
          .sort((a, b) => a.order - b.order),
      ),
      onError,
    ),
    enabled,
  )
  return { staff: rows, loading, error }
}

export type StaffEdit = Partial<Pick<StaffMember,
  | 'name' | 'email' | 'position' | 'supervisorId' | 'supervisorName' | 'managerId' | 'team'
  | 'containerId' | 'licence' | 'userType' | 'accessLevel' | 'personaGroup'
  | 'salesforceNeeds' | 'timeZone' | 'phone' | 'existsInLegacy' | 'legacyUsername'
  | 'newOrgUsername' | 'provisionStatus' | 'status' | 'note'
>>

// Title Case throughout — this engagement's house style for field labels (see
// `docs/internal/standards/` in the client documentation portal repo). These used to be
// sentence case while the Details tab's hardcoded labels were already Title Case, so the
// same field read "Access Level" in one tab and "Access level" in another, in the same
// drawer. Fixed here rather than in the Details tab, since this is the one place the
// wording can drift from.
const LABELS: Record<keyof StaffEdit, string> = {
  name: 'Name', email: 'Email', position: 'Position',
  supervisorId: 'Supervisor', supervisorName: 'Supervisor (Not on the Roster)', managerId: 'Salesforce Manager',
  team: 'Team', containerId: 'Container', licence: 'Licence', userType: 'User Type',
  accessLevel: 'Access Level', personaGroup: 'Persona Group',
  salesforceNeeds: 'Salesforce Needs', timeZone: 'Time Zone', phone: 'Phone',
  existsInLegacy: 'In the Legacy Org', legacyUsername: 'Legacy Username',
  newOrgUsername: 'New Org Username', provisionStatus: 'Provisioning',
  status: 'Status', note: 'Note',
}

/**
 * The before/after wording shared by every field-diff in this feature — staff, container
 * and note edits alike. An empty value reads as "(not set)" on the "from" side and
 * "(cleared)" on the "to" side, so a reader can tell "nobody ever answered" apart from
 * "answered, then cleared", which are different facts once the log is being read back
 * months later. Anything else is shown as its own `String(value)`.
 *
 * Pulled out so the wording can only drift once, in one place, instead of three inline
 * copies quietly disagreeing with each other.
 */
export function describeValue(value: unknown, side: 'from' | 'to'): string {
  if (value === null || value === undefined || value === '') {
    return side === 'to' ? '(cleared)' : '(not set)'
  }
  return String(value)
}

/**
 * Name lookups for turning an id stored on the record into the name a client would
 * recognise. Optional everywhere it is used, and an id with no entry in the map falls back
 * to the id itself rather than to an empty string — so the history always says *something*,
 * even before a caller has the roster loaded to build a lookup from.
 */
export type NameLookup = {
  containers?: Map<string, string>
  staff?: Map<string, string>
}

/**
 * Split out from `saveStaff` so the wording is testable without Firestore.
 *
 * The wording matters more than it looks. Six months from now "who said Debbie could edit
 * other people's events, and when" is not a memory question, and "Access level: (not set)
 * -> Edit" with a name and a date against it is the answer. An empty string on both sides
 * of an arrow is not — and neither is a raw id like "sales" or "bart-blair" to a client who
 * has never seen the schema. `names` is optional and pure (no Firestore reads happen here):
 * the caller passes whatever lookups it already has loaded, and an id missing from the
 * lookup — or no lookup at all — just falls back to the raw id, never to blank.
 */
export function staffChanges(member: StaffMember, edit: StaffEdit, names?: NameLookup) {
  const resolve = (key: keyof StaffEdit, id: string): string => {
    if (key === 'containerId') return names?.containers?.get(id) ?? id
    if (key === 'supervisorId' || key === 'managerId') return names?.staff?.get(id) ?? id
    return id
  }

  const show = (key: keyof StaffEdit, value: unknown, side: 'from' | 'to') => {
    if (key === 'existsInLegacy') return value ? 'Yes' : 'No'
    if (value === null || value === undefined || value === '') {
      // Reads the same on either side of the arrow, deliberately: a person with no
      // container is not on the chart, whether that is where they started or where they
      // ended up. Everything else distinguishes "never answered" from "answered, then
      // cleared", which are different things for somebody reading the history back.
      if (key === 'containerId') return '(not on the chart)'
      return describeValue(value, side)
    }
    if (key === 'containerId' || key === 'supervisorId' || key === 'managerId') return resolve(key, String(value))
    return String(value)
  }

  const changes: Array<{ field: string; from: string; to: string }> = []
  for (const [key, value] of Object.entries(edit) as Array<[keyof StaffEdit, unknown]>) {
    const before = member[key] as unknown
    if (before === value) continue
    changes.push({
      field: LABELS[key],
      from: show(key, before, 'from'),
      to: show(key, value, 'to'),
    })
  }
  return changes
}

/** Mirrors `saveOwner`: write the edit, then one activity entry per field that moved. */
export async function saveStaff(
  member: StaffMember,
  edit: StaffEdit,
  actor: Actor,
  names?: NameLookup,
): Promise<void> {
  const changes = staffChanges(member, edit, names)
  await updateDoc(doc(db, 'staff', member.id), { ...edit })
  for (const change of changes) {
    await recordChange(['staff', member.id], change, actor)
  }
}

/**
 * Turns a name into the lowercase, hyphenated shape every id in this feature already uses
 * — "Jo Smith" becomes "jo-smith", matching existing ids like `bart-blair` and `sales-ops`.
 * A name that collapses to nothing (blank, or pure punctuation) falls back to "item" rather
 * than handing Firestore an empty document id, which it rejects outright.
 */
function slugify(name: string): string {
  const slug = name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
  return slug || 'item'
}

/**
 * Picks a document id for a newly created staff member or container from their name,
 * de-duplicating against every id already in use.
 *
 * This matters because `setDoc` overwrites silently: handing it an id that already exists
 * replaces that document with no error and no warning, which is a far worse failure than an
 * ugly id. So a second "Jo Smith" becomes `jo-smith-2` rather than clobbering the first
 * `jo-smith`, and a third becomes `jo-smith-3` — walking the numeric suffix up past whatever
 * is already taken, rather than trusting the count of existing rows to predict the next free
 * number, which would be wrong the moment a row had ever been removed.
 *
 * Pure and exported on its own — no Firestore, no async — so this, the one part of
 * `createStaff`/`createContainer` actually worth testing carefully, is testable directly.
 */
export function allocateSlug(name: string, existingIds: Iterable<string>): string {
  const taken = new Set(existingIds)
  const base = slugify(name)
  if (!taken.has(base)) return base
  let n = 2
  while (taken.has(`${base}-${n}`)) n++
  return `${base}-${n}`
}

/**
 * Adds a new person to the roster from the chart canvas.
 *
 * Every field this engagement still has an open question about — licence, access level,
 * persona group, the new-org username, provisioning, whether they exist in the legacy org,
 * the decisions that settled any of it — is seeded empty rather than guessed. Being drawn
 * on a card answers none of those questions; a guessed `Not started` that happened to
 * already be true, or a guessed licence that happened to be wrong, would look exactly like
 * a real answer to the next person reading the roster, and there would be no way to tell
 * the difference months later. `order` is set to one past whatever the current maximum
 * actually is, not to the length of the roster — the array's length would collide with an
 * existing order the moment any row had ever been removed, and a collision means two cards
 * fighting for the same sort position.
 *
 * `names` is the same optional lookup `saveStaff` already takes, reused rather than
 * reinvented, so the one activity entry this writes says which lane the person landed in by
 * name, not by raw container id, whenever the caller has that lookup loaded.
 */
export async function createStaff(
  input: { name: string; containerId: string | null; supervisorId?: string | null },
  actor: Actor,
  names?: NameLookup,
): Promise<string> {
  const name = input.name.trim()
  const snap = await getDocs(collection(db, 'staff'))
  const existingIds = snap.docs.map((d) => d.id)
  const maxOrder = snap.docs.reduce(
    (max, d) => Math.max(max, (d.data() as { order?: number }).order ?? 0),
    0,
  )
  const id = allocateSlug(name, existingIds)

  const member: Omit<StaffMember, 'id'> = {
    name,
    email: '',
    position: '',
    supervisorId: input.supervisorId ?? null,
    supervisorName: '',
    managerId: null,
    team: '',
    containerId: input.containerId,
    licence: null,
    // Not one of the "answered in the app" fields the brief calls out by name — those are
    // Salesforce-specific questions this roster tracks. A general employment category has
    // to be something (`UserType` carries no null), and most people added straight onto the
    // chart are this engagement's own team, so `Internal` is the default rather than a guess
    // about an open question.
    userType: 'Internal',
    accessLevel: null,
    personaGroup: null,
    salesforceNeeds: '',
    timeZone: '',
    phone: '',
    existsInLegacy: false,
    legacyUsername: '',
    newOrgUsername: '',
    provisionStatus: 'Not started',
    // A person created with nowhere on the chart to go yet reads as such, the same
    // distinction the roster already draws for Mimi Ferritti (see `StaffStatus`) — not as
    // `Active`, which would claim a placement that has not happened.
    status: input.containerId === null ? 'Not yet placed' : 'Active',
    note: '',
    order: maxOrder + 1,
    decisionIds: [],
  }
  await setDoc(doc(db, 'staff', id), member)

  const lane = input.containerId === null
    ? '(not on the chart)'
    : (names?.containers?.get(input.containerId) ?? input.containerId)
  await recordChange(
    ['staff', id],
    { field: 'Added', from: '(not set)', to: name, note: `Placed in ${lane}` },
    actor,
  )

  return id
}

/**
 * Takes a person off the roster.
 *
 * Refuses before any write when anybody's `supervisorId` still points at this person —
 * naming every one of them, not just counting them. Deleting a supervisor out from under
 * their reports would silently sever that whole subtree from the org chart: every direct
 * report would lose their own line up the tree, and nothing on the canvas would ever say
 * why they suddenly have no supervisor. This is the same judgment call `removeContainer`
 * makes for a lane that still holds people (see its own comment in `useOrgChart.ts`),
 * applied to a person instead of a lane: a refusal somebody can act on — reassign the
 * reports to another supervisor, then try again — beats a silent orphaning every time.
 *
 * Takes just the id, not a `StaffMember` the caller already has loaded — unlike
 * `removeContainer`, which trusts the `staff` array `ChartCanvas` already has in state, this
 * reads the roster itself before deciding, the same "do not trust a caller's possibly-stale
 * copy" judgment `insertContainerBetween` and `addLink` already make elsewhere in this
 * feature for a write that refuses conditionally. Reading the WHOLE `staff` collection
 * (rather than a targeted `where('supervisorId', '==', id)` query) is the same choice
 * `createStaff` already makes a few lines up in this file: the roster this engagement
 * tracks is 35 rows, the same order of magnitude `readContainers()` in `useOrgChart.ts`
 * reasons about for `orgContainers`, not the thousands-row case `moveToContainer` chunks a
 * targeted query for.
 */
export async function removeStaff(id: string, actor: Actor, names?: NameLookup): Promise<void> {
  const docs = (await getDocs(collection(db, 'staff'))).docs
  const target = docs.find((d) => d.id === id)
  const targetData = target?.data() as Partial<StaffMember> | undefined
  const targetName = targetData?.name ?? id

  const reports = docs.filter(
    (d) => (d.id !== id) && (d.data() as { supervisorId?: string | null }).supervisorId === id,
  )
  if (reports.length > 0) {
    const reportNames = reports.map((d) => (d.data() as { name?: string }).name ?? d.id)
    throw new Error(
      `${targetName} still supervises ${reportNames.join(', ')} — reassign them to another `
      + 'supervisor first',
    )
  }

  const managed = docs.filter(
    (d) => (d.id !== id) && (d.data() as { managerId?: string | null }).managerId === id,
  )
  if (managed.length > 0) {
    const managedNames = managed.map((d) => (d.data() as { name?: string }).name ?? d.id)
    throw new Error(
      `${targetName} is the Salesforce Manager of ${managedNames.join(', ')} — pick another `
      + 'manager for them first',
    )
  }

  await deleteDoc(doc(db, 'staff', id))

  const lane = !targetData?.containerId
    ? '(not on the chart)'
    : (names?.containers?.get(targetData.containerId) ?? targetData.containerId)
  await recordChange(
    ['staff', id],
    { field: 'Removed', from: targetName, to: '(not set)', note: `Was in ${lane}` },
    actor,
  )
}
